import departmentService from 'ApiService/SuperAdmin/departmentService';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FaRegEdit, FaTrash } from 'react-icons/fa';
import { BallTriangle } from 'react-loader-spinner';
import InputField from 'components/fields/InputField';

const Index = () => {
  const { state } = useContext(AuthContext);

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(''); // "update" | "delete"
  const [updatedDepartment, setUpdatedDepartment] = useState(null);

  // 🔹 FETCH DEPARTMENTS
  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await departmentService.getAllDepartments();

      if (response.data.success) {
        setDepartments(response.data.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch departments'
      );
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state?.user) fetchDepartments();
  }, [state?.user]);

  // 🔹 OPEN UPDATE MODAL
  const handleUpdateClick = (department) => {
    setUpdatedDepartment({ ...department });
    setShowUpdateModal(true);
  };

  // 🔹 OPEN DELETE CONFIRMATION
  const handleDeleteClick = (department) => {
    setUpdatedDepartment(department);
    setConfirmType('delete');
    setShowConfirmModal(true);
  };

  // 🔹 CONFIRM ACTION (UPDATE / DELETE)
  const handleConfirmAction = async () => {
    setShowConfirmModal(false);

    try {
      let response;

      if (confirmType === 'update') {
        response = await departmentService.updateDepartment(
          updatedDepartment._id,
          { name: updatedDepartment.name.trim() }
        );
      }

      if (confirmType === 'delete') {
        response = await departmentService.deleteDepartment(
          updatedDepartment._id
        );
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowUpdateModal(false);
        fetchDepartments();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Action failed');
    }
  };

  return (
    <div className="mt-10 h-full w-full rounded-[20px] md:mt-5">
      {/* 🔹 LOADING */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <BallTriangle color="#5431ed" />
        </div>
      ) : departments.length === 0 ? (
        <div className="flex h-40 items-center justify-center">
          <Nodatafound title="No Departments found" />
        </div>
      ) : (
        <div className="grid w-full grid-cols-1 gap-4 p-2 md:grid-cols-2 lg:grid-cols-3">
          {departments.map((department) => (
            <div
              key={department._id}
              className="relative rounded-lg border-2 border-brand-500 p-4 dark:border-gray-700 dark:bg-navy-800"
            >
              {/* ACTIONS */}
              <div className="absolute right-2 top-2 flex gap-3 text-lg">
                <button
                  onClick={() => handleUpdateClick(department)}
                  className="text-gray-900 dark:text-white"
                >
                  <FaRegEdit />
                </button>
                <button
                  onClick={() => handleDeleteClick(department)}
                  className="text-red-500"
                >
                  <FaTrash />
                </button>
              </div>

              <h5 className="text-2xl font-bold text-gray-900 dark:text-white">
                {department.name}
              </h5>
            </div>
          ))}
        </div>
      )}

      {/* 🔹 UPDATE MODAL */}
      {showUpdateModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-navy-800">
            <button
              onClick={() => setShowUpdateModal(false)}
              className="absolute right-3 top-2 text-3xl dark:text-white"
            >
              &times;
            </button>

            <h2 className="mb-4 text-lg font-semibold dark:text-white">
              Update Department
            </h2>

            <InputField
              label="Department Name"
              type="text"
              value={updatedDepartment?.name || ''}
              onChange={(e) =>
                setUpdatedDepartment({
                  ...updatedDepartment,
                  name: e.target.value,
                })
              }
            />

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="rounded-lg border px-4 py-2 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmType('update');
                  setShowConfirmModal(true);
                }}
                disabled={!updatedDepartment?.name?.trim()}
                className="rounded-lg bg-brand-500 px-4 py-2 text-white disabled:opacity-50"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 CONFIRMATION MODAL */}
      {showConfirmModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 dark:bg-navy-800">
            <h3 className="mb-4 text-lg font-semibold dark:text-white">
              Confirm {confirmType === 'delete' ? 'Delete' : 'Update'}
            </h3>

            <p className="mb-6 text-sm dark:text-gray-300">
              {confirmType === 'delete'
                ? `Are you sure you want to delete "${updatedDepartment?.name}"?`
                : `Are you sure you want to update the department to "${updatedDepartment?.name}"?`}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="rounded-lg border px-4 py-2 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`rounded-lg px-4 py-2 text-white ${
                  confirmType === 'delete' ? 'bg-red-500' : 'bg-brand-500'
                }`}
              >
                Yes, {confirmType === 'delete' ? 'Delete' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
